/**
 * Shopify cart-form posts that become a checkout without a checkout button (D255).
 *
 * A theme's cart form posts to `/{locale}/cart`; when its data carries a `checkout` field,
 * Shopify applies the form's fields to the cart and opens its checkout. The click and submit
 * interceptors see a checkout **button**; this module covers the post that has none behind it — a
 * theme's `form.requestSubmit()` with no submitter, a hidden `checkout` field, a theme's
 * `form.submit()` (Navigation API only). Every such post goes to Peak (D327; owner, 2026-10-26:
 * "Everything should go to Peak."). The fields `POST /{locale}/cart/update.js` documents reach
 * the cart first: `updates` (by line key or variant id, or positional), `note`, `attributes` and
 * `discount` — "You can submit a serialized cart form", with a `FormData` body as the example,
 * and the answer is "The JSON of the cart" (https://shopify.dev/docs/api/ajax/reference/cart,
 * read 2026-10-16). Every other field is dropped, whatever it is.
 *
 * What that drops, beside the form's own noise (`checkout`, `form_type`, `utf8`), a nested
 * upsell form's `id` / `quantity` (D283) and a theme add-on's toggle such as
 * `packageprotection=on` (D327): line item properties (`properties[…]`), a selling plan
 * (`selling_plan`) and lines (`items[…]`) — the line inputs of `/cart/add` (same reference, read
 * 2026-10-26) — a PayPal or `return_to` redirect (`goto_pp`, `return_to`), and any file the cart
 * form uploads. Shopify's own post might have applied some of them; that has not been verified,
 * and the checkout Peak opens sells the cart as `update.js` leaves it, without them. Until D327
 * these kept the post native (D283's list), reported as `form_field_unsupported`.
 *
 * Also the query of a `/cart/checkout` link (D372, `linkUpdateFields`): its `note`, `attributes[…]`
 * and `discount` reach the cart through the same `update.js` before the handoff — an app such as
 * Intelligems writes `attributes[igId]` onto the theme's checkout link. Never `updates[…]`.
 *
 * Pure and DOM-free so it is unit-testable: `tests/unit/storefront/cart-form.test.ts`.
 */

/** `/cart`, locale prefix optional, trailing slash allowed. */
const CART_PATH = /^(\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?cart)\/?$/i;

/** The fields `update.js` documents, in their form-field spelling. */
const UPDATE_FIELD = /^(?:updates\[[^\]]*\]|note|attributes\[[^\]]+\]|discount)$/;

/** The subset a `/cart/checkout` link's query may carry (D372): no quantities. */
const LINK_FIELD = /^(?:note|attributes\[[^\]]+\]|discount)$/;

/**
 * The `update.js` URL for a form posting to `action` from `pageHref`: `/en/cart` →
 * `/en/cart/update.js`. `null` unless the action resolves to the page's own origin and a cart
 * path. A missing action posts to the page itself, as in the browser.
 */
export function cartUpdateUrlOf(action: string | null, pageHref: string): string | null {
  try {
    const page = new URL(pageHref);
    const url = new URL(action ?? '', page);
    const match = url.origin === page.origin ? CART_PATH.exec(url.pathname) : null;
    return match === null ? null : (match[1] as string) + '/update.js';
  } catch {
    return null;
  }
}

/**
 * The form's fields for `update.js`, in order; `[]` when there is nothing to update. `skip`, the
 * submitter's own name, is not part of the cart. Kept: an `UPDATE_FIELD` with a string value.
 * Dropped: everything else — a file even under an `update.js` name, and every other name (D327,
 * which ended D283's native list: no field sends the post to Shopify any more).
 */
export function cartUpdateFields(
  entries: readonly (readonly [string, unknown])[],
  skip: string | null,
): [string, string][] {
  const fields: [string, string][] = [];
  for (const [name, value] of entries) {
    if (name !== skip && typeof value === 'string' && UPDATE_FIELD.test(name)) {
      fields.push([name, value]);
    }
  }
  return fields;
}

/**
 * The cart fields of a `/cart/checkout` link's query (`search`, with or without its `?`), in
 * order, for `update.js` (D372): `note`, `attributes[<name>]` and `discount` — the parameters
 * `/cart/update.js` documents (https://shopify.dev/docs/api/ajax/reference/cart, read 2026-10-30),
 * which are also the `note`, `attributes` and `discount` query parameters Shopify documents for a
 * cart permalink that opens checkout (https://shopify.dev/docs/apps/build/checkout/create-cart-permalinks,
 * read 2026-10-30). Every other parameter is dropped: `updates[…]` (a link sets no quantities),
 * `locale`, tracking parameters, an empty-key `attributes[]`. `[]` when there is nothing to carry.
 */
export function linkUpdateFields(search: string): [string, string][] {
  const fields: [string, string][] = [];
  new URLSearchParams(search).forEach((value, name) => {
    if (LINK_FIELD.test(name)) fields.push([name, value]);
  });
  return fields;
}
